import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Slot } from './simShared';
import { loadRawAccount } from '../utils/runeStore';
import { accountPayload, slotFromRaw } from './SimFitRunes';

/**
 * **룬 최적화** — 시뮬 탭 공덱 몹 룬 상자의 [룬 최적화] (사용자 2026-10-29 "이 공덱에 대해서 최적의 룬을 찾는거야" ·
 * "json 룬으로 피팅되도록 여러 방덱룬작에 대해 피팅되도록 해").
 *
 * 서버(swSim engine/src/simtune.ts)가 먼저 [내 룬 자동할당]처럼 지금 칸 능력치에 계정 JSON 룬·아티·유물을 맞추고,
 * 몹마다 맞추기 목표(체·공·방·치피 · 공속 · 치확 · 효적 · 효저 — 세트는 그대로)를 한 가지씩 바꿔 **계정 룬으로 다시 맞춰** 본다.
 * 판은 방덱 실전 룬작 여러 개(방덱 100팀 × 20판), 점수는 어려운 방덱 50% 승률 — 가장 많이 오른 바꿈을 받고, 더 오르지 않으면 멈춘다.
 * 끝나면 다른 판(확인 판)으로 처음 덱과 바꾼 덱을 다시 쳐서 정말 올랐으면 칸에 내 룬을 끼운다. [되돌리기]로 처음 칸.
 * 계정 JSON 은 룬 옵티마이저 탭에서 불러온 것(SimFitRunes 와 같다).
 */
export interface SimRuneTuneHandle { start: () => void }

interface TuneView {
    status: 'running' | 'done' | 'failed' | 'cancelled';
    round: number; stage: string; tuneWin0: number; tuneWin: number; seconds: number;
    log: Array<{ mob: number; desc: string; from: number; to: number }>;
    result?: { hard0: number; hard: number; win0: number; win: number; start: any[]; mobs: any[] };
    error?: string;
}

const pct = (x: number) => `${(x * 100).toFixed(1)}%`;


const SimRuneTune = forwardRef<SimRuneTuneHandle, {
    /** 서버로 보낼 칸 (시뮬레이션과 같은 모양) — 칸이 덜 찼으면 null */
    payload: () => { atk: unknown[]; def: unknown[] } | null;
    /** 지금 공덱 칸 — 결과를 이 칸에 끼운다 */
    slots: Slot[];
    isMobile: boolean;
    /** tuned — 맞춘 내 룬을 끼웠다(참) · [되돌리기](거짓) */
    onApply: (slots: Slot[], tuned: boolean) => void;
    onBusy: (busy: boolean) => void;
}>(function SimRuneTune({ payload, slots, isMobile, onApply, onBusy }, ref) {
    const [view, setView] = useState<TuneView | null>(null);
    const [err, setErr] = useState('');
    const [id, setId] = useState<string | null>(null);
    /** 시작할 때 칸 — 결과는 이 칸에, [되돌리기]도 이 칸으로 */
    const startSlots = useRef<Slot[] | null>(null);
    const [applied, setApplied] = useState(false);
    const busy = !!id && view?.status === 'running';
    useEffect(() => { onBusy(busy); }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps

    // 진행 묻기
    useEffect(() => {
        if (!id) return;
        let alive = true;
        const tick = async () => {
            try {
                const r = await fetch(`/api/sim/rune-tune/${id}`, { credentials: 'include' });
                const d = await r.json();
                if (!alive) return;
                if (!d?.success) throw new Error(d?.error || '룬 최적화 진행을 읽지 못했습니다');
                setView(d);
                if (d.status !== 'running') {
                    setId(null);
                    if (d.status === 'failed') setErr(d.error || '룬 최적화에 실패했습니다');
                    // 확인 판에서도 올랐으면 끼운다
                    if (d.status === 'done' && d.result && d.log.length && d.result.hard > d.result.hard0 && startSlots.current) {
                        onApply(startSlots.current.map((s, i) => (d.result.mobs[i] ? slotFromRaw(s, d.result.mobs[i]) : s)), true);
                        setApplied(true);
                    }
                    return;
                }
            } catch (e: any) {
                if (alive) { setErr(e.message || '룬 최적화 진행을 읽지 못했습니다'); setId(null); }
                return;
            }
            if (alive) timer = setTimeout(tick, 1500);
        };
        let timer = setTimeout(tick, 800);
        return () => { alive = false; clearTimeout(timer); };
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async () => {
        if (busy) return;
        const p = payload();
        if (!p) { setErr('공덱·방덱 세 칸씩을 먼저 채워 주세요'); return; }
        const a = await loadRawAccount();
        if (!a) { setErr('룬 옵티마이저 탭에서 계정 JSON 을 먼저 불러와 주세요 (예전에 불러왔다면 한 번 더 불러와야 합니다)'); return; }
        setErr(''); setView(null); setApplied(false);
        startSlots.current = slots;
        try {
            const r = await fetch('/api/sim/rune-tune', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ ...p, account: accountPayload(a) })
            });
            const d = await r.json();
            if (!d?.success || !d.id) throw new Error(d?.error || '룬 최적화를 시작하지 못했습니다');
            setView(d);
            setId(d.id);
        } catch (e: any) {
            setErr(e.message || '룬 최적화를 시작하지 못했습니다');
        }
    };
    const stop = async () => {
        if (!id) return;
        try { await fetch(`/api/sim/rune-tune/${id}/cancel`, { method: 'POST', credentials: 'include' }); } catch { /* 다음 물음에서 본다 */ }
    };

    useImperativeHandle(ref, () => ({ start }));

    if (!view && !err) return null;
    const name = (i: number) => slots[i]?.pick?.name || startSlots.current?.[i]?.pick?.name || `${i + 1}번`;
    const card: React.CSSProperties = {
        border: '1.5px solid #caa052', borderRadius: 12, background: 'rgba(40,26,16,0.85)', color: '#f3e3c0',
        padding: isMobile ? '8px 10px' : '10px 14px', margin: isMobile ? '0 0 8px' : '0 0 12px', fontSize: isMobile ? 11.5 : 13, lineHeight: 1.6
    };
    const small: React.CSSProperties = {
        border: '1.5px solid #caa052', borderRadius: 8, background: 'rgba(40,26,16,0.7)', color: '#ffd98a',
        fontSize: isMobile ? 10.5 : 12, fontWeight: 800, padding: isMobile ? '2px 8px' : '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap'
    };
    const v = view;
    return (
        <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                <b style={{ color: '#ffd98a' }}>
                    {busy ? `룬 최적화 중 · ${v?.stage || ''}` : v?.status === 'done' ? '룬 최적화 끝' : v?.status === 'cancelled' ? '룬 최적화 멈춤' : '룬 최적화'}
                    {v ? ` · ${v.seconds}초` : ''}
                </b>
                {busy && <button type="button" style={small} onClick={stop}>멈추기</button>}
                {!busy && applied && startSlots.current && (
                    <button type="button" style={small} onClick={() => { onApply(startSlots.current!, false); setApplied(false); }}>되돌리기</button>
                )}
            </div>
            {v && busy && v.round > 0 && <div>어려운 방덱 50% 승률 {pct(v.tuneWin0)} → <b style={{ color: '#ffd98a' }}>{pct(v.tuneWin)}</b></div>}
            {v?.status === 'done' && v.result && (
                v.log.length && v.result.hard > v.result.hard0
                    ? <div>확인 판 어려운 방덱 50% {pct(v.result.hard0)} → <b style={{ color: '#7ee08a' }}>{pct(v.result.hard)}</b> · 전체 {pct(v.result.win0)} → {pct(v.result.win)} — 맞춘 내 룬을 칸에 끼웠습니다</div>
                    : <div>더 나은 룬을 찾지 못했습니다 — 확인 판 어려운 방덱 50% {pct(v.result.hard0)} · 전체 {pct(v.result.win0)}{v.log.length ? ` (바꾼 덱 ${pct(v.result.hard)})` : ''}</div>
            )}
            {v && v.log.length > 0 && (
                <div style={{ marginTop: 2, color: '#d9c7a0' }}>
                    {v.log.map((l, i) => <div key={i}>· {name(l.mob)} {l.desc} <span style={{ opacity: 0.75 }}>({pct(l.from)} → {pct(l.to)})</span></div>)}
                </div>
            )}
            {err && <div style={{ color: '#ff8a80', fontWeight: 700 }}>{err}</div>}
        </div>
    );
});
export default SimRuneTune;
